import { useCallback, useEffect, useState } from 'react'
import { useAppStore } from '../stores/app-store'
import { ralphApi } from '../api'
import { getTerminalHandle } from './OutputTerminal'
import type { DeliveryResult, RunStatus } from '../shared/types'

export function ActionBar() {
  const {
    status,
    userPrompt,
    systemPrompt,
    maxIterations,
    completionPromise,
    maxBudgetUsd,
    llmProvider,
    deliveryMode,
    selectedRepoFullName,
    selectedBaseBranch,
    clearPrd,
    setStatus,
    setIteration,
    setStartTime,
    appendPrd,
    setCurrentRun
  } = useAppStore()

  const [publishing, setPublishing] = useState(false)

  const isRunning = status === 'running' || status === 'generating-prd'
  const canStart = userPrompt.trim().length > 0 && !isRunning
  const hasSession = ralphApi.sessionId !== null
  const canPublish = hasSession && !isRunning && deliveryMode === 'draft-pr' && selectedRepoFullName.trim().length > 0

  const refreshCurrentRun = useCallback(async (sessionId?: string | null) => {
    const targetSessionId = sessionId ?? ralphApi.sessionId
    if (!targetSessionId) return

    try {
      const run = await ralphApi.getRun(targetSessionId)
      setCurrentRun(run)
    } catch {
      // Ignore transient fetch failures while streaming
    }
  }, [setCurrentRun])

  const handleGeneratePrd = useCallback(async () => {
    clearPrd()
    setStatus('generating-prd')
    setStartTime(Date.now())

    await ralphApi.generatePrd(userPrompt, systemPrompt, llmProvider, {
      onChunk: (text) => {
        appendPrd(text)
      },
      onComplete: () => {
        setStatus('idle')
        setStartTime(null)
      }
    })
  }, [appendPrd, clearPrd, setStartTime, setStatus, systemPrompt, userPrompt])

  const handleStartLoop = useCallback(async () => {
    setStatus('running')
    setStartTime(Date.now())
    setIteration(0, maxIterations)
    setCurrentRun(null)

    const terminal = getTerminalHandle()

    await ralphApi.startLoop(
      {
        backend: 'codex',
        llmProvider,
        systemPrompt,
        userPrompt,
        maxIterations,
        completionPromise,
        maxBudgetUsd,
        repoFullName: selectedRepoFullName || undefined,
        baseBranch: selectedBaseBranch || undefined,
        deliveryMode
      },
      {
        onSession: (sessionId) => {
          void refreshCurrentRun(sessionId)
        },
        onChunk: (chunk) => {
          terminal?.writeChunk(chunk)
        },
        onIterationStart: (data) => {
          setIteration(data.iteration, data.maxIterations)
          terminal?.writeIteration(data.iteration, data.maxIterations)
        },
        onComplete: (state) => {
          setStatus(state.status as RunStatus)
          setStartTime(null)
          void refreshCurrentRun()
          if (state.error) {
            terminal?.writeChunk({
              type: 'error',
              content: state.error,
              timestamp: Date.now()
            })
          }
        },
        onStatusChange: (nextStatus) => {
          setStatus(nextStatus as RunStatus)
        }
      }
    )
  }, [
    completionPromise,
    deliveryMode,
    llmProvider,
    maxBudgetUsd,
    maxIterations,
    refreshCurrentRun,
    selectedBaseBranch,
    selectedRepoFullName,
    setCurrentRun,
    setIteration,
    setStartTime,
    setStatus,
    systemPrompt,
    userPrompt
  ])

  const handleCancel = useCallback(async () => {
    await ralphApi.cancelLoop()
    setStatus('cancelled')
    setStartTime(null)
    void refreshCurrentRun()
  }, [refreshCurrentRun, setStartTime, setStatus])

  const handleDownload = useCallback(() => {
    const url = ralphApi.getDownloadUrl()
    if (url) {
      window.open(url, '_blank')
    }
  }, [])

  const writeDeliveryResult = useCallback((result: DeliveryResult) => {
    const terminal = getTerminalHandle()
    if (result.success) {
      terminal?.writeChunk({
        type: 'status',
        content: `Created draft PR: ${result.url || selectedRepoFullName}`,
        timestamp: Date.now()
      })
      return
    }

    terminal?.writeChunk({
      type: 'error',
      content: `GitHub publish failed: ${result.error}`,
      timestamp: Date.now()
    })
  }, [selectedRepoFullName])

  const handlePublishGithub = useCallback(async () => {
    if (!selectedRepoFullName.trim()) return

    setPublishing(true)
    setStatus('publishing')

    const result = await ralphApi.deliverToGithub(
      selectedRepoFullName,
      selectedBaseBranch || 'main'
    )

    setPublishing(false)
    setStatus('complete')
    writeDeliveryResult(result)
    void refreshCurrentRun()
  }, [refreshCurrentRun, selectedBaseBranch, selectedRepoFullName, setStatus, writeDeliveryResult])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter' && canStart) {
        e.preventDefault()
        void handleStartLoop()
      }
      if (e.key === 'Escape' && isRunning) {
        e.preventDefault()
        void handleCancel()
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [canStart, handleCancel, handleStartLoop, isRunning])

  return (
    <div className="card action-bar">
      <div className="action-row">
        <button className="btn btn-outline" onClick={() => void handleGeneratePrd()} disabled={!canStart}>
          Generate PRD
        </button>

        <button className="btn btn-primary" onClick={() => void handleStartLoop()} disabled={!canStart}>
          Start Loop
        </button>

        {isRunning && (
          <button className="btn btn-danger" onClick={() => void handleCancel()}>
            Cancel <span className="kbd">Esc</span>
          </button>
        )}
      </div>

      {hasSession && !isRunning && (
        <div className="action-row action-row-secondary">
          <button className="btn btn-outline btn-sm" onClick={handleDownload}>
            Download Bundle
          </button>

          <button
            className="btn btn-outline btn-sm"
            onClick={() => void handlePublishGithub()}
            disabled={!canPublish || publishing}
          >
            {publishing ? 'Publishing...' : 'Create Draft PR'}
          </button>
        </div>
      )}
    </div>
  )
}
